import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * A message about the state of the thing on screen: a lifted `surface` box
 * with the status glyph and, per the design system, an inset ring in the
 * state's subtle tint rather than a border — the same treatment `FxPanel`
 * gives a panel that carries a state.
 *
 * The glyph is never the only signal: red and green differ mostly by hue, so
 * the tone always arrives with the text beside it, and `danger` speaks as an
 * `alert` while the calmer tones are a `status`.
 */
const tones = {
  info: { icon: 'info', ring: 'ring-primary-subtle', iconColor: 'text-primary-text' },
  success: { icon: 'check_circle', ring: 'ring-success-subtle', iconColor: 'text-success-text' },
  warning: { icon: 'error', ring: 'ring-warning-subtle', iconColor: 'text-warning-text' },
  danger: { icon: 'cancel', ring: 'ring-danger-subtle', iconColor: 'text-danger-text' },
} as const

export function FxAlert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: keyof typeof tones
  title?: string
  children?: React.ReactNode
  className?: string
}) {
  const { icon, ring, iconColor } = tones[tone]
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex gap-4 rounded-sm bg-surface p-4 text-body text-text shadow-raised ring-2 ring-inset',
        ring,
        className,
      )}
    >
      <FxIcon name={icon} size={22} className={cn('shrink-0', iconColor)} />
      <div className="space-y-1">
        {title && <p className="text-body-strong">{title}</p>}
        {children}
      </div>
    </div>
  )
}
